"use client";
import { Track } from "@/src/lib/tracking";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { safeStringifyError } from "utils";
import type { StartTrackingResult } from "@components/Chat/InteractiveTool/types";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import { trackerQueryKeys } from "./queryKeys";

/**
 * POST /trackers/{trackerId}/activate — INACTIVE → ACTIVE（Chat start_tracking 成功路径）。
 */
export function useActivateTracker() {
  const { trackerApi } = useOspApiClientProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (trackerId: string): Promise<StartTrackingResult> => {
      const trimmed = trackerId.trim();
      if (!trimmed) {
        throw new Error("[useActivateTracker] trackerId is required");
      }

      if (!trackerApi) {
        throw new Error("[useActivateTracker] trackerApi unavailable");
      }

      Track.log("info", "useActivateTracker", "activate request", {
        trackerId: trimmed,
      });
      const res = await trackerApi.request<{ data?: StartTrackingResult }>({
        path: `/trackers/${encodeURIComponent(trimmed)}/activate`,
        method: "POST",
        secure: true,
        format: "json",
      });

      if (res?.error) {
        Track.log("warn", "useActivateTracker", "activate failed", {
          cause: String(safeStringifyError(res.error)),
        });
        throw res.error;
      }

      const body = res.data as
        StartTrackingResult | { data?: StartTrackingResult } | undefined;
      const payload =
        body && "data" in body && body.data
          ? body.data
          : (body as StartTrackingResult | undefined);
      const resolvedTrackerId =
        typeof payload?.trackerId === "string" ? payload.trackerId : trimmed;

      return {
        trackerId: resolvedTrackerId,
        status: "ACTIVE",
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: trackerQueryKeys.all });
    },
  });
}
